/**
 * THEME PROVIDER - DARK MODE MANAGEMENT
 * ====================================
 * 
 * This component manages the application's theme state (light/dark mode).
 * It provides context to all child components so they can access and modify
 * the current theme.
 * 
 * FEATURES:
 * - Simple toggle between light and dark modes
 * - Persists theme preference to localStorage
 * - Provides theme toggle functionality
 * - Applies theme class to document body
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { readStoredValue, writeStoredValue } from "@/lib/safe-storage";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme = "light" }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);

  // Update theme and remember the choice
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    writeStoredValue("theme", newTheme);
  };

  // Toggle between light and dark
  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  // Initialize theme from localStorage
  useEffect(() => {
    const savedTheme = readStoredValue("theme");
    if (savedTheme === "light" || savedTheme === "dark") {
      setThemeState(savedTheme);
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (typeof window !== "undefined") {
      const root = document.documentElement;
      
      // Remove existing theme classes
      root.classList.remove("light", "dark");
      
      // Add the appropriate theme class
      root.classList.add(theme);
    }
  }, [theme]);

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use theme context
// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider; only fast refresh is affected.
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}